import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { Button } from '@/components/ui/button';
import { Sun, Moon } from 'lucide-react';
import SearchModal from './SearchModal';
import { authService } from '../features/auth/authService';
import { isMiniApp } from '../telegram';
import { useTheme } from '../theme';

const ThemeToggle = ({ className = '' }: { className?: string }) => {
  const [theme, toggle] = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
      className={`flex items-center justify-center rounded-[14px] bg-muted text-foreground transition-colors hover:bg-accent ${className}`}
    >
      {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
};

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
};

const loginIcon = (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3"
    />
  </svg>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart);
  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  // In the Telegram mini-app auth is automatic (via initData) and there is no
  // login screen, so the account entry always points at the profile — never a
  // "Войти" button. On the website it depends on the real auth state.
  const isAuthenticated = authService.isAuthenticated() || isMiniApp();

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const accountItem: NavItem = isAuthenticated
    ? {
        to: '/profile',
        label: 'Профиль',
        icon: (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-9 12a5 5 0 0 1 10 0"
            />
          </svg>
        ),
      }
    : {
        to: '/login',
        label: 'Войти',
        icon: loginIcon,
      };

  const navigationItems: NavItem[] = [
    accountItem,
    {
      to: '/favorites',
      label: 'Избранное',
      icon: (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="m12 20.5-1.45-1.32C5.4 14.5 2 11.36 2 7.5A4.5 4.5 0 0 1 6.5 3 5 5 0 0 1 12 6.1 5 5 0 0 1 17.5 3 4.5 4.5 0 0 1 22 7.5c0 3.86-3.4 7-8.55 11.68L12 20.5Z"
          />
        </svg>
      ),
    },
    {
      to: '/cart',
      label: 'Корзина',
      icon: (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M3 4h2.5l1.6 8.2a1 1 0 0 0 1 .8h8.7a1 1 0 0 0 .95-.68L20 7H7.1M9 19a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm8 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
          />
        </svg>
      ),
      badge: cartCount > 0 ? cartCount : undefined,
    },
  ];

  const mobileMenuItems = navigationItems.filter((item) => item.to !== '/cart');

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 border-b border-foreground/5 bg-card/95 backdrop-blur-xl">
        <div className="mx-auto hidden max-w-[90rem] items-center gap-4 px-4 py-4 lg:flex">
          {/* Fake search input — opens modal on focus/click */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Открыть поиск"
            className="flex min-w-0 max-w-[34rem] flex-1 items-center overflow-hidden rounded-[22px] border border-[#ef4444]/55 bg-muted h-[56px] text-left transition-colors hover:border-[#ef4444]/80 hover:bg-accent"
          >
            <span className="flex h-full w-[56px] shrink-0 items-center justify-center bg-[linear-gradient(135deg,#dc2626,#ef4444)]">
              <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
            </span>
            <span className="px-5 text-base text-foreground/35">Найти игру или подписку</span>
          </button>

          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 text-xl font-semibold tracking-[0.08em] text-foreground transition-opacity hover:opacity-80"
          >
            FreeApp
          </Link>

          <nav className="ml-auto flex shrink-0 items-center gap-2">
            {navigationItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="relative flex h-[56px] items-center gap-2.5 rounded-[20px] bg-muted px-5 text-[15px] font-medium text-foreground transition-colors hover:bg-accent"
              >
                <span className="text-[#ef4444]">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="absolute right-2.5 top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
            <ThemeToggle className="h-[56px] w-[56px]" />
          </nav>
        </div>

        <div className="mx-auto max-w-[90rem] px-4 py-4 lg:hidden">
          <div className="relative h-11 sm:h-12">
            <div className="absolute left-0 top-0 z-10 flex items-center gap-1 sm:gap-2">
              <Button
                type="button"
                onClick={() => setIsMenuOpen((value) => !value)}
                aria-label="Открыть меню"
                aria-expanded={isMenuOpen}
                aria-controls="header-burger-menu"
                variant="ghost"
                className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[linear-gradient(135deg,#dc2626,#ef4444)] text-white transition-opacity hover:opacity-90"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </Button>

              <Link
                to="/catalog"
                aria-label="Открыть поиск"
                className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[linear-gradient(135deg,#dc2626,#ef4444)] text-white transition-opacity hover:opacity-90"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                </svg>
              </Link>

            </div>

            <Link
              to="/"
              className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap px-1 text-sm font-semibold tracking-[0.12em] text-foreground"
            >
              FreeApp
            </Link>

            <ThemeToggle className="absolute right-12 sm:right-14 top-0 z-10 h-11 w-11 sm:h-12 sm:w-12" />

            <Link
              to="/cart"
              aria-label="Открыть корзину"
              className="absolute right-0 top-0 z-10 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-muted text-foreground transition-colors hover:bg-accent"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M3 4h2.5l1.6 8.2a1 1 0 0 0 1 .8h8.7a1 1 0 0 0 .95-.68L20 7H7.1M9 19a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm8 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"
                />
              </svg>
              {cartCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-semibold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {isMenuOpen && (
          <>
            <Button
              type="button"
              aria-label="Закрыть меню"
              onClick={closeMenu}
              variant="ghost"
              className="fixed inset-0 z-40 h-auto w-auto rounded-none bg-black/60 p-0 hover:bg-black/60"
            />

            <div
              id="header-burger-menu"
              className="absolute left-4 top-[calc(100%+0.75rem)] z-50 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-gray-800 bg-gray-950 shadow-2xl"
            >
              <div className="border-b border-gray-800 px-5 py-4">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Меню</p>
                <p className="mt-1 text-sm text-gray-300">Быстрый переход по разделам</p>
              </div>

              <nav className="flex flex-col gap-2 p-3">
                {mobileMenuItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    className="relative flex h-[52px] items-center gap-3 rounded-[20px] bg-muted px-5 text-[15px] font-medium text-foreground transition-colors hover:bg-accent"
                  >
                    <span className="text-[#ef4444]">{item.icon}</span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="absolute right-2.5 top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>
            </div>
          </>
        )}
      </div>

      <div className="h-[76px] sm:h-20 lg:h-[89px]" />

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
